import { useEffect, useState } from "react";
import { CheckIcon, SearchIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { cn } from "@/src/lib/utils";
import { useDebounce } from "@/src/lib/useDebounce";
import { useResizeObserver } from "@/src/lib/useResizeObserver";
import { Input } from "./input";

export type ComboboxProps<T> = {
  className?: string;
  renderOption?: (value: T) => string;
  loadOptions: (query: string) => Promise<T[]> | T[];
  onChange?: (value: T | null) => void;
  value?: T | null;
  placeholder?: string;
  allowAddNewItem?: boolean;
  onCreate?: (name: string, value: string) => void;
  createLabel?: (inputValue: string) => string;
};

export function ComboboxWithAdd<T>(props: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const [items, setItems] = useState<T[]>([]);
  const [width, setWidth] = useState(0);

  const renderValue = (v: T) => (props.renderOption ? props.renderOption(v) : String(v));

  const search = useDebounce(async (v: string) => {
    setItems(await props.loadOptions(v));
  }, 300);
  const onQueryChange = async (v: string) => {
    setQuery(v);
    search(v);
  };

  useEffect(() => {
    onQueryChange(props.value ? renderValue(props.value) : "");
  }, []);

  const triggerRef = useResizeObserver(([e]) => {
    setWidth(e.target.getBoundingClientRect().width);
  });

  return (
    <Popover
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <PopoverTrigger className={props.className} asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between bg-white hover:bg-white", props.className)}
        >
          <span>{props.value ? renderValue(props.value) : props.placeholder}</span>
          <SearchIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[200px] p-0", props.className)} style={{ width }}>
        <div className="flex w-full flex-col rounded-md bg-popover text-popover-foreground">
          <div className="flex items-center px-3 border-b">
            <input
              className="flex w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 h-9"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              role="combobox"
            />
            <SearchIcon className="h-4 w-4 shrink-0 opacity-50" />
          </div>
          {items?.length === 0 && props.allowAddNewItem && query && (
            <>
              {!isCreatingNew ? (
                <>
                  {items?.length === 0 && props.allowAddNewItem && query && (
                    <div
                      className="relative flex justify-between cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                      role="option"
                      onClick={() => setIsCreatingNew(true)} // Switch to create mode
                    >
                      {props.createLabel ? props.createLabel(query) : `Add "${query}"`}
                    </div>
                  )}
                  {items?.length === 0 && !props.allowAddNewItem && (
                    <div className="px-3 py-8 text-center text-sm text-popover-foreground">No results</div>
                  )}
                  {items?.length > 0 && (
                    <div
                      className="max-h-[300px] p-1 overflow-y-auto overflow-x-hidden"
                      role="listbox"
                      aria-label="Suggestions"
                    >
                      {items.map((item, i) => (
                        <div
                          key={i}
                          className="relative flex justify-between cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                          role="option"
                          onClick={() => {
                            props.onChange?.(props.value === item ? null : item);
                            setOpen(false);
                          }}
                        >
                          {renderValue(item)}
                          {props.value && renderValue(props.value) === renderValue(item) && <CheckIcon size={16} />}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col px-3 py-4">
                  <Input
                    className="mb-2 w-full"
                    placeholder="Enter account name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Input
                    className="mb-2 w-full"
                    placeholder="Enter account number"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                  />
                  <Button
                    onClick={() => {
                      setOpen(false);
                      setIsCreatingNew(false);
                      props.onCreate && props.onCreate(name, number);
                    }}
                    disabled={!name || !number}
                  >
                    Add Account
                  </Button>
                </div>
              )}
            </>
          )}

          {items?.length === 0 && !props.allowAddNewItem && (
            <div className="px-3 py-8 text-center text-sm text-popover-foreground">No results</div>
          )}
          {items?.length > 0 && (
            <div
              className="max-h-[300px] p-1 overflow-y-auto overflow-x-hidden"
              role="listbox"
              aria-label="Suggestions"
            >
              {items.map((item, i) => (
                <div
                  key={i}
                  className="relative flex justify-between cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                  role="option"
                  onClick={() => {
                    props.onChange?.(props.value === item ? null : item);
                    setOpen(false);
                  }}
                >
                  {renderValue(item)}
                  {props.value && renderValue(props.value) === renderValue(item) && <CheckIcon size={16} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
